import React from 'react';
import { Document, Page, Text, Font, View, StyleSheet } from '@react-pdf/renderer';
import CoverPage from './sections/CoverPage';
import DisclaimerPage from './sections/DisclaimerPage';
import FullTableOfContentsPage from './sections/FullTableOfContentsPage';
import DocumentReferencePage from './sections/DocumentReferencePage';
import ExecutiveSummaryPage from './sections/ExecutiveSummaryPage';
import KeyFindingsPage from './sections/KeyFindingsPage';
import RecommendationsPage from './sections/RecommendationsPage';
import ScopePage from './sections/ScopePage';
import ProjectObjectivesPage from './sections/ProjectObjectivesPage';
import SummaryOfFindingsPage from './sections/SummaryOfFindingsPage';
import VulnerabilityRatingDefinitionsPage from './sections/VulnerabilityRatingDefinitionsPage';
import KeyFindingsListPage from './sections/KeyFindingsListPage';
import DetailedFindingsCoverPage from './sections/DetailedFindingsCoverPage';
import DetailedFindingsBySeverityPage from './sections/DetailedFindingsBySeverityPage';
import AppendixOwaspRiskRatingPages from './sections/AppendixOwaspRiskRatingPages';
import MethodologyPage from './sections/MethodologyPage';
import NetworkMethodologyPage from './sections/NetworkMethodologyPage';
import MobileMethodologyPage from './sections/MobileMethodologyPage';
import SourceCodeMethodologyPage from './sections/SourceCodeMethodologyPage';
import AboutUsPage from './sections/AboutUsPage';
import { ReportData, FindingConfig } from '../types/report.types';
import { SectionPageProvider, useSectionPages } from './SectionPageContext';
import { isFindingClosed, getFindingStableKey, isFindingActuallyOpen, filterReportDataByRunId } from '../utils/findingsUtils';
import { processDisclaimerContent, registerReportFonts, sortFindings } from '../utils/reportSharedUtils';

// Register fonts once globally
registerReportFonts();

// Helper to filter and group findings based on configuration
const processFindingsForPDF = (reportData: ReportData, passedProcessedFindings?: Record<string, any[]> | null) => {
  const config: FindingConfig = reportData.finding_config || {
    globalFields: {
      description: true,
      instructions: true,
      impact: true,
      fix: true,
      evidence: true
    },
    individualFindings: {}
  };

  const detailedFindings = (reportData.detailed_findings || []).map((f, idx) => ({
    ...f,
    _stableKey: getFindingStableKey(f, idx)
  }));

  // Enforce mandatory stable sort: Severity -> Date -> ID
  const sortedFindings = sortFindings(detailedFindings);
  
  // New granular filtering logic
  const filteredFindings = sortedFindings.filter(f => {
    const idStr = (f as any)._stableKey;
    const indConfig = config.individualFindings?.[idStr];
    
    // If individual config exists, respect its 'included' property
    if (indConfig) {
      return indConfig.included;
    }
    
    // Default: Only include OPEN findings if no individual override exists
    return isFindingActuallyOpen(reportData, f);
  });

  // Group by severity
  const grouped: Record<string, any[]> = {
    CRITICAL: [],
    HIGH: [],
    MEDIUM: [],
    LOW: [],
    INFO: []
  };

  filteredFindings.forEach(f => {
    const sev = (f.severity_category || 'INFO').toUpperCase();
    if (grouped[sev]) {
      grouped[sev].push(f);
    } else {
      grouped.INFO.push(f);
    }
  });

  return {
    groupedFindings: grouped,
    config
  };
};

interface FullReportTemplatePDFProps {
  reportData: ReportData;
  pieChartImage?: string;
  barChartImage?: string;
  processedFindings?: Record<string, any[]> | null;
  processing?: boolean;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: 'grey',
  },
});

const FullReportTemplatePDFContent: React.FC<FullReportTemplatePDFProps> = ({
  reportData: rawReportData,
  pieChartImage,
  barChartImage,
  processedFindings: passedProcessedFindings,
  processing,
}) => {
  const reportData = React.useMemo(() => filterReportDataByRunId(rawReportData) || rawReportData, [rawReportData]);
  const { groupedFindings } = processFindingsForPDF(reportData, passedProcessedFindings);
  const disclaimerParagraphs = processDisclaimerContent((reportData.disclaimer ?? '') || '');
  const { updateSectionPage } = useSectionPages();
  
  // Use groupedFindings instead of passedProcessedFindings if the latter is null or if we want to enforce config
  const activeFindings = passedProcessedFindings || groupedFindings;
  const hasOpenDetailed = Object.values(activeFindings).some(arr => (arr || []).length > 0);

  if (processing) {
    return (
      <Document>
        <Page>
          <Text>Loading...</Text>
        </Page>
      </Document>
    );
  }
  // Helper to render a page with footer
  const renderPageWithFooter = (children: React.ReactNode, key?: string | number) => (
    <React.Fragment key={key}>
      {React.cloneElement(children as React.ReactElement, {},
        <>
          {(children as React.ReactElement).props.children}
          <View style={styles.footer} fixed>
            <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
          </View>
        </>
      )}
    </React.Fragment>
  );

  return (
    <Document>
      <CoverPage reportData={reportData} />
      <DisclaimerPage
        reportData={reportData}
        disclaimerParagraphs={disclaimerParagraphs}
        processDisclaimerContent={processDisclaimerContent}
        sectionId="Disclaimer"
      />
      <FullTableOfContentsPage reportData={reportData} />
      {/* Start adding footer from here */}
      {renderPageWithFooter(<DocumentReferencePage reportData={reportData} sectionId="DocumentReference" />, 'doc-ref')}
      {renderPageWithFooter(<ExecutiveSummaryPage
        reportData={reportData}
        pieChartImage={pieChartImage}
        barChartImage={barChartImage}
        sectionId="ExecutiveSummary"
      />, 'exec-summary')}
      {renderPageWithFooter(<KeyFindingsPage reportData={reportData} sectionId="KeyFindings" />, 'key-findings')}
      {renderPageWithFooter(<RecommendationsPage reportData={reportData} sectionId="Recommendations" />, 'recommendations')}
      {renderPageWithFooter(<ScopePage reportData={reportData} sectionId="Scope" />, 'scope')}
      {renderPageWithFooter(<ProjectObjectivesPage reportData={reportData} sectionId="ProjectObjectives" />, 'objectives')}
      {renderPageWithFooter(<SummaryOfFindingsPage
        reportData={reportData}
        pieChartImage={pieChartImage}
        barChartImage={barChartImage}
        sectionId="SummaryOfFindings"
      />, 'summary-findings')}
      {renderPageWithFooter(<VulnerabilityRatingDefinitionsPage reportData={reportData} sectionId="VulnerabilityRatingDefinitions" />, 'vuln-defs')}
      {renderPageWithFooter(<KeyFindingsListPage reportData={reportData} sectionId="KeyFindingsList" />, 'key-findings-list')}
      {/* Render these directly, as they have their own footers */}
      {hasOpenDetailed && (
        <>
          <DetailedFindingsCoverPage documentNumber={reportData.document_number} branding_logo={reportData.branding_logo} sectionId="DetailedFindingsCover" />
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map(severity =>
            <DetailedFindingsBySeverityPage
              key={severity}
              severity={severity.charAt(0) + severity.slice(1).toLowerCase()}
              findings={activeFindings?.[severity] || []}
              documentNumber={reportData.document_number}
              reportData={reportData}
              registerSectionPage={updateSectionPage}
              sectionId={`DetailedFindings_${severity.charAt(0) + severity.slice(1).toLowerCase()}`}
            />
          )}
        </>
      )}
      {AppendixOwaspRiskRatingPages({ documentNumber: reportData.document_number, registerSectionPage: updateSectionPage, sectionId: 'AppendixOwaspRiskRating_Page1' })
        .filter(React.isValidElement)
        .map((page, idx) => React.cloneElement(page, { key: `appendix-${idx}` }))}
      {(reportData.methodology?.web) && (
        <MethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="Methodology" />
      )}
      {(reportData.methodology?.network) && (
        <NetworkMethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="NetworkMethodology" />
      )}
      {(reportData.methodology?.mobile) && (
        <MobileMethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="MobileMethodology" />
      )}
      {(reportData.methodology?.sourceCode) && (
        <SourceCodeMethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="SourceCodeMethodology" />
      )}
      <AboutUsPage />
    </Document>
  );
};

const FullReportTemplatePDF: React.FC<FullReportTemplatePDFProps> = (props) => (
  <SectionPageProvider>
    <FullReportTemplatePDFContent {...props} />
  </SectionPageProvider>
);

export default FullReportTemplatePDF; 